<template>
    <Teleport to="body">
        <Transition name="overlay">
            <div v-if="isOpen" class="chat-overlay" @click="closePanel"></div>
        </Transition>
        <Transition name="panel">
            <div v-if="isOpen" class="chat-panel" :class="{ 'mobile-panel': isMobile }">
                <div class="chat-header">
                    <div class="header-content">
                        <div class="ai-badge">
                            <span class="material-symbols-outlined">smart_toy</span>
                        </div>
                        <div class="header-text">
                            <h3>AI Assistant</h3>
                            <span class="status-text">Powered by Palazzo AI</span>
                        </div>
                    </div>
                    <div class="header-actions">
                        <button
                            class="icon-btn"
                            @click="handleStartNewChat"
                            title="Start new chat (saves current conversation)"
                        >
                            <span class="material-symbols-outlined">refresh</span>
                        </button>
                        <button
                            v-if="hasMessages"
                            class="icon-btn"
                            @click="handleClearHistory"
                            title="Clear chat history"
                        >
                            <span class="material-symbols-outlined">delete</span>
                        </button>
                        <button class="icon-btn" @click="closePanel" title="Close chat">
                            <span class="material-symbols-outlined">close</span>
                        </button>
                    </div>
                </div>

                <div class="chat-body" ref="chatBody">
                    <div v-if="isLoading && !hasMessages" class="loading-state">
                        <div class="loading-spinner"></div>
                        <span>Loading chat history...</span>
                    </div>

                    <div v-else-if="!hasMessages" class="empty-state">
                        <div class="empty-icon">
                            <span class="material-symbols-outlined">chat</span>
                        </div>
                        <h4>Welcome to AI Assistant</h4>
                        <p>Ask me anything about your billings, events, or clients. I'll help you find the information you need.</p>
                    </div>

                    <div v-else class="messages-container">
                        <ChatMessage
                            v-for="message in messages"
                            :key="message.id"
                            :message="message"
                        />
                    </div>

                    <div v-if="isLoading && hasMessages" class="typing-indicator">
                        <span class="dot"></span>
                        <span class="dot"></span>
                        <span class="dot"></span>
                    </div>
                </div>

                <div class="chat-footer">
                    <ChatSuggestions
                        v-if="!hasMessages"
                        :disabled="isLoading"
                        @select="handleSuggestionSelect"
                    />
                    <div class="input-area">
                        <ChatInput
                            v-model="inputText"
                            :disabled="isLoading"
                            @send="handleSend"
                        />
                        <div v-if="saveStatus" class="save-indicator" :class="'status-' + saveStatus">
                            <span v-if="saveStatus === 'saving'" class="material-symbols-outlined spin">hourglass_empty</span>
                            <span v-else-if="saveStatus === 'saved'" class="material-symbols-outlined">check_circle</span>
                            <span class="indicator-text">{{ saveStatus === 'saving' ? 'Thinking...' : 'Saved' }}</span>
                        </div>
                    </div>
                </div>
            </div>
        </Transition>
    </Teleport>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import ChatMessage from './ChatMessage.vue'
import ChatInput from './ChatInput.vue'
import ChatSuggestions from './ChatSuggestions.vue'

export default {
    name: 'ChatPanel',
    components: {
        ChatMessage,
        ChatInput,
        ChatSuggestions,
    },
    data() {
        return {
            inputText: '',
            isMobile: false,
            handleResize: () => {
                this.isMobile = window.innerWidth < 768
            },
        }
    },
    computed: {
        ...mapState('chat', {
            messages: 'messages',
            isLoading: 'isLoading',
            saveStatus: 'saveStatus',
        }),
        ...mapGetters('chat', {
            hasMessages: 'hasMessages',
            isPanelOpen: 'isPanelOpen',
        }),
        isOpen() {
            return this.$store.state.chat.isOpen
        },
    },
    watch: {
        messages() {
            this.$nextTick(() => this.scrollToBottom())
        },
        isLoading(val) {
            if (val) {
                this.$nextTick(() => this.scrollToBottom())
            }
        },
        isOpen(val) {
            if (val) {
                this.fetchHistory()
                this.isMobile = window.innerWidth < 768
            }
        },
    },
    methods: {
        ...mapActions('chat', {
            sendMessage: 'sendMessage',
            fetchHistory: 'fetchHistory',
            clearHistory: 'clearHistory',
            startNewChat: 'startNewChat',
            closeChat: 'closeChat',
        }),
        closePanel() {
            this.closeChat()
        },
        async handleSend(content) {
            if (!content.trim()) return
            this.scrollToBottom()
            try {
                await this.sendMessage(content)
            } catch (error) {
                console.error('Failed to send message:', error)
            }
        },
        handleSuggestionSelect(suggestion) {
            this.handleSend(suggestion)
        },
        async handleStartNewChat() {
            this.startNewChat()
        },
        async handleClearHistory() {
            if (!confirm('Are you sure you want to clear your chat history? This will delete all saved messages from the database.')) {
                return
            }
            try {
                await this.clearHistory()
            } catch (error) {
                console.error('Failed to clear history:', error)
            }
        },
        scrollToBottom() {
            const chatBody = this.$refs.chatBody
            if (chatBody) {
                chatBody.scrollTop = chatBody.scrollHeight
            }
        },
    },
    mounted() {
        window.addEventListener('resize', this.handleResize)
    },
    beforeUnmount() {
        window.removeEventListener('resize', this.handleResize)
    },
}
</script>

<style scoped>
.chat-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.3);
    z-index: 9998;
}

.chat-panel {
    position: fixed;
    top: 0;
    right: 0;
    width: 400px;
    max-width: 100%;
    height: 100vh;
    background: #ffffff;
    box-shadow: -8px 0 32px rgba(52, 48, 43, 0.1);
    z-index: 9999;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.mobile-panel {
    width: 100%;
}

.overlay-enter-active,
.overlay-leave-active {
    transition: opacity 0.3s ease;
}

.overlay-enter-from,
.overlay-leave-to {
    opacity: 0;
}

.panel-enter-active,
.panel-leave-active {
    transition: transform 0.3s ease;
}

.panel-enter-from,
.panel-leave-to {
    transform: translateX(100%);
}

.chat-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 20px;
    background: linear-gradient(135deg, var(--color-primary) 0%, var(--color-primary-container) 100%);
    color: var(--color-on-primary);
}

.header-content {
    display: flex;
    align-items: center;
    gap: 12px;
}

.ai-badge {
    width: 44px;
    height: 44px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.2);
    display: flex;
    align-items: center;
    justify-content: center;
}

.ai-badge .material-symbols-outlined {
    font-size: 24px;
}

.header-text h3 {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
    font-family: 'Plus Jakarta Sans', sans-serif;
}

.status-text {
    font-size: 12px;
    opacity: 0.8;
}

.header-actions {
    display: flex;
    gap: 8px;
}

.icon-btn {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    border: none;
    background: rgba(0, 0, 0, 0.2);
    color: white;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background 0.2s ease;
}

.icon-btn:hover {
    background: rgba(0, 0, 0, 0.35);
}

.icon-btn .material-symbols-outlined {
    font-size: 20px;
}

.chat-body {
    flex: 1;
    overflow-y: auto;
    padding: 20px;
    background: var(--color-surface-container-lowest);
}

.loading-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
    gap: 16px;
    color: var(--color-outline);
}

.loading-spinner {
    width: 32px;
    height: 32px;
    border: 3px solid var(--color-outline-variant);
    border-top-color: var(--color-primary);
    border-radius: 50%;
    animation: spin 1s linear infinite;
}

@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}

.empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
    text-align: center;
    padding: 20px;
}

.empty-icon {
    width: 64px;
    height: 64px;
    border-radius: 50%;
    background: var(--color-surface-container-low);
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 16px;
}

.empty-icon .material-symbols-outlined {
    font-size: 32px;
    color: var(--color-primary);
}

.empty-state h4 {
    margin: 0 0 8px;
    font-size: 18px;
    font-weight: 600;
    color: var(--color-on-surface);
    font-family: 'Plus Jakarta Sans', sans-serif;
}

.empty-state p {
    margin: 0;
    font-size: 14px;
    color: var(--color-outline);
    line-height: 1.5;
}

.messages-container {
    display: flex;
    flex-direction: column;
}

.typing-indicator {
    display: flex;
    gap: 4px;
    padding: 12px 16px;
    background: var(--color-surface-container-low);
    border-radius: 18px;
    width: fit-content;
    margin-top: 8px;
}

.typing-indicator .dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: var(--color-outline);
    animation: bounce 1.4s infinite ease-in-out both;
}

.typing-indicator .dot:nth-child(1) {
    animation-delay: -0.32s;
}

.typing-indicator .dot:nth-child(2) {
    animation-delay: -0.16s;
}

@keyframes bounce {
    0%,
    80%,
    100% {
        transform: scale(0);
    }
    40% {
        transform: scale(1);
    }
}

.chat-footer {
    border-top: 1px solid var(--color-outline-variant);
}

.input-area {
    padding: 12px 16px;
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.save-indicator {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 11px;
    font-family: 'Manrope', sans-serif;
    animation: fadeIn 0.2s ease-in;
}

.save-indicator .material-symbols-outlined {
    font-size: 14px;
}

.indicator-text {
    font-weight: 500;
}

.status-saved {
    color: var(--color-secondary);
}

.status-saving {
    color: var(--color-outline);
}

@keyframes fadeIn {
    from {
        opacity: 0;
        transform: translateY(4px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.spin {
    animation: spinIcon 1s linear infinite;
}

@keyframes spinIcon {
    to {
        transform: rotate(360deg);
    }
}
</style>